// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import { STEP_NAME, isPlantStep, utils } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '../../lib/base';
import { AddCircleOutlineIcon } from '../../lib/icons';
import { DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import type {
  CropGrowTimeDataQuery,
  CropGrowTimeDataQueryVariables,
  EditCropGrowTimeStepsMutation,
  EditCropGrowTimeStepsMutationVariables,
} from '../../types/__generated__/graphql';
import { getNumericID, getSecondsFromDaysStringRequired, getTimeRanges, validateNumInput } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import NewCropStepForm, {
  type CropStepFormInput,
  type Props as NewCropStepFormProps,
} from '../cropStep/NewCropStepForm';
import { formatCropDialogTitle } from './formatCropDialogTitle';

const EDIT_CROP_GROW_TIME_STEPS_MUTATION = gql`
  mutation EditCropGrowTimeSteps(
    $crop_id: Int!
    $total_grow_time: Int!
    $create_crop_steps: [CreateCropCropStepInput!]!
  ) {
    editCropGrowTimeSteps(
      input: {
        crop_id: $crop_id
        total_grow_time: $total_grow_time
        create_crop_steps: $create_crop_steps
      }
    ) {
      updated_crop {
        id
        total_grow_time
        updated_at
        steps {
          edges {
            node {
              id
              name
              time_to_start_after_first_step
              description
            }
          }
        }
      }
      updated_tasks {
        id
        task_time
        task_name
        notes
        updated_at
      }
      updated_crop_steps {
        id
        name
        description
        time_to_start_after_first_step
      }
      new_tasks {
        id
      }
      new_crop_steps {
        id
      }
      deleted_tasks {
        id
      }
      deleted_crop_steps {
        id
      }
    }
  }
`;

const CROP_GROW_TIME_DATA = gql`
  query CropGrowTimeData($id: ID!) {
    crop(id: $id) {
      id
      total_grow_time
      steps {
        edges {
          node {
            id
            name
            time_to_start_after_first_step
            description
          }
        }
      }
    }
  }
`;

interface Props {
  readonly cropId: string;
  readonly cropName: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function EditCropGrowTimeStepsDialog({ isOpen, onClose, onSuccess, cropId, cropName }: Props) {
  const [fetchCrop, { data, error: dataError, loading: cropDataLoading }] = useLazyQuery<
    CropGrowTimeDataQuery,
    CropGrowTimeDataQueryVariables
  >(CROP_GROW_TIME_DATA, {
    fetchPolicy: 'network-only',
  });
  const [editCropGrowTimeSteps, { loading: editCropLoading, client: apolloClient }] = useMutation<
    EditCropGrowTimeStepsMutation,
    EditCropGrowTimeStepsMutationVariables
  >(EDIT_CROP_GROW_TIME_STEPS_MUTATION);
  const { timezone } = useUserContext();
  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);

  const secondsToDays = React.useCallback((seconds: number) => seconds / 86400, []);

  React.useEffect(() => {
    if (isOpen) {
      void fetchCrop({
        variables: { id: cropId },
        onCompleted: (cropData) => {
          const crop = cropData?.crop;
          if (crop != null) {
            const cropGrowDuration = secondsToDays(crop.total_grow_time).toString();
            setDuration(cropGrowDuration);
            const cropStepForms = crop.steps.edges.map(({ node }) => ({
              maybeExistingId: getNumericID(node.id),
              name: node.name,
              timeToStartAfterFirstStep:
                // If the user originally set the harvest step to be different than
                // grow time, then fix that here
                node.name === STEP_NAME.HARVEST
                  ? cropGrowDuration
                  : secondsToDays(node.time_to_start_after_first_step).toString(),
              description: node.description ?? '',
              nameValidation: '',
              timeToStartAfterFirstStepValidation: '',
            }));
            cropStepForms.sort(
              (a, b) =>
                getSecondsFromDaysStringRequired(a.timeToStartAfterFirstStep) -
                getSecondsFromDaysStringRequired(b.timeToStartAfterFirstStep),
            );
            const formsHavePlantStep = crop.steps.edges.some(({ node }) => isPlantStep(node.name));
            setFormsHavePlantStep(formsHavePlantStep);
            setCropStepForms(
              cropStepForms.map((values, idx) => {
                const formKey = utils.getRandomInt();
                return (
                  <NewCropStepForm
                    key={formKey}
                    formKey={formKey}
                    values={values}
                    index={idx}
                    disableFields={disableFields}
                    onChangeField={handleCropStepFormInputChange}
                    onRemove={removeCropStepForm}
                    formsHavePlantStep={formsHavePlantStep}
                  />
                );
              }),
            );
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  }, [isOpen, cropId, fetchCrop, secondsToDays]);

  const disableFields = cropDataLoading || editCropLoading;

  const [formsHavePlantStep, setFormsHavePlantStep] = React.useState(false);
  const [cropGrowDuration, setDuration] = React.useState('');
  const [cropStepForms, setCropStepForms] = React.useState<React.ReactElement<NewCropStepFormProps>[]>([]);

  const [cropGrowDurationValidation, setDurationValidation] = React.useState('');
  const durationValidationMessage =
    'Crop step start time cannot be larger than total grow time. Change total grow time or crop step time.';
  const onDurationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDuration(e.target.value);
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setDurationValidation(msg),
      isRequired: true,
    });
  };
  const onDurationBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    const secondsAsNum = Math.max(0, Number.parseFloat(newVal));
    const time = secondsAsNum.toString();
    setDuration(time);
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setDurationValidation(msg),
      isRequired: true,
    });

    // Now validate that total grow time is greater than all cropStep times
    // For any cropStepForm that is greater than total grow time, set the error on that form
    setCropStepForms((prevForms) => {
      const updatedForms = [...prevForms];

      return updatedForms.map((form) =>
        React.cloneElement(form, {
          values: {
            ...form.props.values,
            timeToStartAfterFirstStepValidation:
              Number.parseFloat(form.props.values.timeToStartAfterFirstStep) > secondsAsNum
                ? durationValidationMessage
                : '',
          },
        }),
      );
    });
  };

  const addCropStepForm = () => {
    setCropStepForms((prevForms) => {
      const formKey = utils.getRandomInt();
      return [
        <NewCropStepForm
          key={formKey}
          formKey={formKey}
          index={0}
          disableFields={disableFields}
          values={{
            name: '',
            nameValidation: '',
            timeToStartAfterFirstStep: '',
            description: '',
            timeToStartAfterFirstStepValidation: '',
          }}
          formsHavePlantStep={formsHavePlantStep}
          onChangeField={handleCropStepFormInputChange}
          onRemove={removeCropStepForm}
          // onEnter={onEnter}
        />,
        ...prevForms.map((form, idx) => React.cloneElement(form, { index: idx + 1 })),
      ];
    });
  };

  function removeCropStepForm(index: number) {
    setCropStepForms((prevForms) => {
      const updatedForms = [...prevForms];
      const formRemoved = updatedForms.splice(index, 1);
      let plantStepRemoved = false;
      // If the removed form was a plant step, then set formsHavePlantStep to false
      if (formRemoved.length > 0) {
        plantStepRemoved = isPlantStep(formRemoved[0].props.values.name);
      }
      if (plantStepRemoved) {
        setFormsHavePlantStep(false);
      }

      const final = updatedForms.map((old, idx) => {
        return React.cloneElement(old, {
          index: idx, // Need to update the indexes
          formsHavePlantStep: plantStepRemoved ? false : old.props.formsHavePlantStep,
        });
      });

      return final;
    });
  }
  React.useEffect(() => {
    // Every time cropGrowDuration changes
    // Re-create all the cropStepForms to give them new handleCropStepFormInputChange function
    // Also update the input for the harvest step
    setCropStepForms((prevForms) => {
      const updatedForms = [...prevForms];
      return updatedForms.map((form) => {
        const name = form.props.values.name;

        return React.cloneElement(form, {
          ...form.props,
          values: {
            ...form.props.values,
            timeToStartAfterFirstStep:
              name === STEP_NAME.HARVEST ? cropGrowDuration : form.props.values.timeToStartAfterFirstStep,
          },
          onChangeField: handleCropStepFormInputChange,
        });
      });
    });
  }, [cropGrowDuration]);

  function handleCropStepFormInputChange(
    index: number,
    fieldName: keyof CropStepFormInput,
    value: string,
    validationFieldName?: 'timeToStartAfterFirstStepValidation' | 'nameValidation',
    validationValue?: string,
  ) {
    setCropStepForms((prevForms) => {
      // If new value is a plant step then set to true
      // If new value is not a plant step AND prev value WAS a plant step then set to false
      let newFormsHavePlantStep = formsHavePlantStep;
      if (fieldName === 'name') {
        const prevValue = prevForms[index].props.values[fieldName];
        const newIsPlant = isPlantStep(value);
        if (newIsPlant) {
          newFormsHavePlantStep = true;
        } else if (prevValue !== undefined && isPlantStep(prevValue) && !isPlantStep(value)) {
          newFormsHavePlantStep = false;
        }
      }
      setFormsHavePlantStep(newFormsHavePlantStep);

      const updatedForms = [...prevForms];

      const newValues = {
        ...prevForms[index].props.values,
        [fieldName]: value,
      };
      if (validationFieldName !== undefined && validationValue !== undefined) {
        newValues[validationFieldName] = validationValue;
      }
      if (fieldName === 'timeToStartAfterFirstStepValidation') {
        const seconds = getSecondsFromDaysStringRequired(value);
        const growTimeSeconds = getSecondsFromDaysStringRequired(cropGrowDuration);
        if (seconds > growTimeSeconds) {
          newValues.timeToStartAfterFirstStepValidation = durationValidationMessage;
        } else if (!Number.isInteger(Number(value))) {
          newValues.timeToStartAfterFirstStepValidation = 'When to start must be an integer number of days';
        } else {
          newValues.timeToStartAfterFirstStepValidation = '';
        }
      }

      updatedForms[index] = React.cloneElement(updatedForms[index], {
        values: newValues,
      });

      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const finalForms: React.ReactElement<any, string | React.JSXElementConstructor<any>>[] = updatedForms.map(
        (oldForm) => {
          return React.cloneElement(oldForm, {
            formsHavePlantStep: newFormsHavePlantStep,
          });
        },
      );

      return finalForms;
    });
  }

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    const cropSteps = cropStepForms.map((form) => {
      const { name, timeToStartAfterFirstStep, description } = form.props.values;
      return {
        existing_id: form.props.values.maybeExistingId,
        name,
        time_to_start_after_first_step: getSecondsFromDaysStringRequired(timeToStartAfterFirstStep),
        description: description === '' ? null : description,
      };
    });

    void editCropGrowTimeSteps({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
      ],
      update: (cache, { data }) => {
        const tasksCreated = (data?.editCropGrowTimeSteps?.new_tasks?.length ?? 0) > 0;
        const tasksDeleted = (data?.editCropGrowTimeSteps?.deleted_tasks?.length ?? 0) > 0;
        const tasksUpdated = (data?.editCropGrowTimeSteps?.updated_tasks?.length ?? 0) > 0;
        cache.modify({
          fields: {
            task_groups(existingGroup, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingGroup;
            },
            tasks(existingTasks, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingTasks;
            },
            taskGroup(existingGroup, { DELETE }) {
              return tasksCreated || tasksDeleted || tasksUpdated ? DELETE : existingGroup;
            },
            // Changing the steps of a crop may change the current stage of a planting group
            planting_groups(existingGroup, { DELETE }) {
              return tasksCreated || tasksCreated ? DELETE : existingGroup;
            },
            plantingGroup(existingGroup, { DELETE }) {
              return tasksCreated || tasksCreated ? DELETE : existingGroup;
            },
            planting(existingGroup, { DELETE }) {
              return tasksCreated || tasksCreated ? DELETE : existingGroup;
            },
          },
        });
      },
      variables: {
        total_grow_time: getSecondsFromDaysStringRequired(cropGrowDuration),
        create_crop_steps: cropSteps,
        crop_id: getNumericID(cropId),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editCropGrowTimeSteps != null) {
          enqueueSnackbar('Updated crop grow cycle', { variant: 'success' });
          onSuccess();
          evictCacheData({
            apolloClient,
            taskIds: dataIn.editCropGrowTimeSteps.deleted_tasks.map((task) => task.id),
            cropStepIds: dataIn.editCropGrowTimeSteps.deleted_crop_steps.map((cropStep) => cropStep.id),
          });
        } else {
          enqueueSnackbar('Error editing crop, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
        setDuration('');
        setDurationValidation('');
        setCropStepForms([]);
        setFormsHavePlantStep(false);
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean =
    cropGrowDurationValidation !== '' ||
    cropStepForms.some(
      ({ props }) => props.values.nameValidation !== '' || props.values.timeToStartAfterFirstStepValidation !== '',
    );
  const requiredFieldsFilled: boolean =
    cropGrowDuration !== '' &&
    cropStepForms.every(({ props }) => props.values.name !== '' && props.values.timeToStartAfterFirstStep !== '');
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;
  const onEnter = () => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  };
  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        aria-describedby="grow-duration"
        id="grow-duration"
        label="Total Grow Time (days)"
        value={cropGrowDuration || ''}
        type="number"
        numberMin={0}
        onChange={onDurationChange}
        onBlur={onDurationBlur}
        onEnter={onEnter}
        subtext={cropGrowDurationValidation}
        error={cropGrowDurationValidation !== ''}
        inputMode="numeric"
        hasSubtext
        required
        disabled={disableFields}
      />
      {cropStepForms.map((cropStepForm, idx) => (
        <Box key={cropStepForm.props.formKey}>{cropStepForm}</Box>
      ))}
      <Box>
        <Grid container display="flex" flexDirection="row" alignItems="center">
          <Tooltip
            title="Add steps to the process of harvesting your crop. Like plant, water, harvest, etc. You can add steps now or later. If you don't create a plant step or harvest step they will be created automatically."
            placement="bottom-end"
          >
            <Grid
              sx={{ borderRadius: (t) => t.shape.borderRadius, cursor: 'pointer' }}
              onClick={() =>
                disableFields
                  ? {
                      // do nothing if clicked while loading. TODO: hacky make better
                    }
                  : addCropStepForm()
              }
              display="flex"
              flexDirection="row"
              alignItems="center"
            >
              <IconButton aria-label="add-crop-step">
                <AddCircleOutlineIcon />
              </IconButton>
              <Typography sx={{ color: (t) => t.palette.grey[600] }}>Add Grow Step</Typography>
            </Grid>
          </Tooltip>
          <Grid flexGrow="1" />
        </Grid>
      </Box>
      <Typography variant="body1">
        Note that editing a crop's grow cycle and grow time will automatically update tasks for planned plantings, but
        not for in progress or completed plantings.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{formatCropDialogTitle('Edit Crop Grow Cycle', cropName)}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        {dataError ? <ErrorView error={dataError} /> : cropDataLoading || data?.crop == null ? <PageLoading /> : form}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
